import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getWidgetConfig, updateWidgetConfig, toggleWidget, getBillingStatus, type BillingStatus } from '../api';
import { getShopDomain } from '../shopify';
import '../widgets/hype-stock-counter.js';

// Form state shape — mirrors the backend config fields exactly
interface StockCounterConfig {
  threshold: number;
  message_template: string;
  sold_out_message: string;
  hide_when_zero: boolean;
  display_style: string;
  bg_color: string;
  text_color: string;
  accent_color: string;
}

interface StylePreset {
  id: string;
  name: string;
  description: string;
  display_style: string;
  bg_color: string;
  text_color: string;
  accent_color: string;
}

const STYLE_PRESETS: StylePreset[] = [
  {
    id: 'warm-yellow',
    name: 'Warm Yellow',
    description: 'Classic urgency badge',
    display_style: 'badge',
    bg_color: '#fff3cd',
    text_color: '#856404',
    accent_color: '#d63384',
  },
  {
    id: 'urgent-red',
    name: 'Urgent Red',
    description: 'High urgency alert',
    display_style: 'badge',
    bg_color: '#fce4e4',
    text_color: '#991b1b',
    accent_color: '#dc2626',
  },
  {
    id: 'neutral-gray',
    name: 'Neutral Gray',
    description: 'Subtle informational',
    display_style: 'badge',
    bg_color: '#f3f4f6',
    text_color: '#374151',
    accent_color: '#6b7280',
  },
  {
    id: 'clean-outline',
    name: 'Clean Outline',
    description: 'Minimal clean look',
    display_style: 'inline',
    bg_color: '#ffffff',
    text_color: '#333333',
    accent_color: '#005bd3',
  },
];

const DEFAULTS: StockCounterConfig = {
  threshold: 10,
  message_template: 'Only {count} left!',
  sold_out_message: 'Sold out',
  hide_when_zero: false,
  display_style: 'badge',
  bg_color: '#fff3cd',
  text_color: '#856404',
  accent_color: '#d63384',
};

type PreviewState = 'low' | 'very-low' | 'sold-out';

@customElement('hype-stock-counter-page')
export class StockCounterPage extends LitElement {
  override createRenderRoot() { return this; }

  // Loading / saving states
  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _loadError = '';
  @state() private _saveSuccess = false;
  @state() private _saveError = '';
  @state() private _saveFlash = false;

  private _flashTimer: ReturnType<typeof setTimeout> | undefined;

  // Widget enabled toggle
  @state() private _enabled = false;

  // Preview state override — does not affect saved config
  @state() private _previewState: PreviewState = 'low';

  // Form fields
  @state() private _threshold = DEFAULTS.threshold;
  @state() private _message_template = DEFAULTS.message_template;
  @state() private _sold_out_message = DEFAULTS.sold_out_message;
  @state() private _hide_when_zero = DEFAULTS.hide_when_zero;
  @state() private _display_style = DEFAULTS.display_style;
  @state() private _bg_color = DEFAULTS.bg_color;
  @state() private _text_color = DEFAULTS.text_color;
  @state() private _accent_color = DEFAULTS.accent_color;

  // Custom CSS (Pro plan)
  @state() private _custom_css = '';
  @state() private _billing: BillingStatus | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadConfig();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this._flashTimer);
  }

  private async _loadConfig(): Promise<void> {
    this._loading = true;
    this._loadError = '';
    try {
      const [widget, billing] = await Promise.all([
        getWidgetConfig('stock_counter'),
        getBillingStatus().catch(() => null),
      ]);
      this._billing = billing;
      this._enabled = widget.enabled;
      this._custom_css = widget.custom_css ?? '';
      const c = widget.config as Partial<StockCounterConfig>;
      this._threshold        = (c.threshold        ?? DEFAULTS.threshold)        as number;
      this._message_template = (c.message_template ?? DEFAULTS.message_template) as string;
      this._sold_out_message = (c.sold_out_message ?? DEFAULTS.sold_out_message) as string;
      this._hide_when_zero   = (c.hide_when_zero   ?? DEFAULTS.hide_when_zero)   as boolean;
      this._display_style    = (c.display_style    ?? DEFAULTS.display_style)    as string;
      this._bg_color         = (c.bg_color         ?? DEFAULTS.bg_color)         as string;
      this._text_color       = (c.text_color       ?? DEFAULTS.text_color)       as string;
      this._accent_color     = (c.accent_color     ?? DEFAULTS.accent_color)     as string;
    } catch (err) {
      this._loadError = err instanceof Error ? err.message : 'Failed to load configuration.';
    } finally {
      this._loading = false;
    }
  }

  private async _handleToggle(enabled: boolean): Promise<void> {
    this._enabled = enabled;
    try {
      const config: Record<string, unknown> = {
        threshold: this._threshold,
        message_template: this._message_template,
        sold_out_message: this._sold_out_message,
        hide_when_zero: this._hide_when_zero,
        display_style: this._display_style,
        bg_color: this._bg_color,
        text_color: this._text_color,
        accent_color: this._accent_color,
      };
      await toggleWidget('stock_counter', enabled, config);
    } catch (err) {
      // Revert optimistic update on failure
      this._enabled = !enabled;
      this._saveError = err instanceof Error ? err.message : 'Failed to toggle widget.';
      this._saveSuccess = false;
    }
  }

  private async _handleSave(): Promise<void> {
    this._saving = true;
    this._saveSuccess = false;
    this._saveError = '';
    try {
      const config: StockCounterConfig = {
        threshold: this._threshold,
        message_template: this._message_template,
        sold_out_message: this._sold_out_message,
        hide_when_zero: this._hide_when_zero,
        display_style: this._display_style,
        bg_color: this._bg_color,
        text_color: this._text_color,
        accent_color: this._accent_color,
      };
      await updateWidgetConfig('stock_counter', config as unknown as Record<string, unknown>, this._custom_css);
      this._saveSuccess = true;
      // Trigger brief success flash on save button + preview pulse
      clearTimeout(this._flashTimer);
      this._saveFlash = true;
      this._flashTimer = setTimeout(() => { this._saveFlash = false; }, 1500);
    } catch (err) {
      this._saveError = err instanceof Error ? err.message : 'Failed to save configuration.';
    } finally {
      this._saving = false;
    }
  }

  private _applyPreset(p: StylePreset): void {
    this._display_style = p.display_style;
    this._bg_color = p.bg_color;
    this._text_color = p.text_color;
    this._accent_color = p.accent_color;
  }

  private _handleColor(key: string, value: string): void {
    if (key === 'bg_color')     this._bg_color     = value;
    if (key === 'text_color')   this._text_color   = value;
    if (key === 'accent_color') this._accent_color = value;
  }

  private _getPreviewQuantity(): string {
    if (this._previewState === 'very-low') return '1';
    if (this._previewState === 'sold-out') return '0';
    return '3';
  }

  override render() {
    if (this._loading) {
      return html`<s-spinner></s-spinner>`;
    }

    if (this._loadError) {
      return html`
        <s-page heading="Stock Counter">
          <s-banner tone="critical">
            <strong>Failed to load configuration</strong><br />
            ${this._loadError}
          </s-banner>
          <s-button @click=${() => void this._loadConfig()}>Retry</s-button>
        </s-page>
      `;
    }

    return html`
      <style>
        hype-stock-counter-page .preview-layout {
          display: grid;
          grid-template-columns: 55fr 45fr;
          gap: 24px;
          align-items: start;
        }
        hype-stock-counter-page .config-column {
          display: flex;
          flex-direction: column;
          gap: var(--p-space-base, 16px);
        }
        hype-stock-counter-page .preview-panel {
          position: sticky;
          top: 80px;
        }
        hype-stock-counter-page .preview-frame {
          border: 1px solid #e3e3e3;
          border-radius: 12px;
          background: #fafafa;
          padding: 24px;
          overflow: hidden;
        }
        hype-stock-counter-page .save-btn-success {
          background-color: #008060 !important;
          border-color: #008060 !important;
          transition: background-color 0.2s, border-color 0.2s;
        }
        @keyframes hype-preview-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(0,128,96,0.5); border-color: #008060; }
          60%  { box-shadow: 0 0 0 6px rgba(0,128,96,0); border-color: #008060; }
          100% { box-shadow: 0 0 0 0 rgba(0,128,96,0); border-color: #e3e3e3; }
        }
        hype-stock-counter-page .preview-frame.preview-pulse {
          animation: hype-preview-pulse 1s ease-out forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          hype-stock-counter-page .save-btn-success {
            transition: none;
          }
          hype-stock-counter-page .preview-frame.preview-pulse {
            animation: none;
            border-color: #008060;
          }
        }
        hype-stock-counter-page .preview-state-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 12px;
        }
        hype-stock-counter-page .preset-grid {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 4px;
        }
        hype-stock-counter-page .preset-card {
          width: 120px;
          cursor: pointer;
          border: 2px solid #e3e3e3;
          border-radius: 8px;
          padding: 10px;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          gap: 6px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        hype-stock-counter-page .preset-card:hover {
          border-color: #b5b5b5;
          box-shadow: 0 2px 6px rgba(0,0,0,0.10);
        }
        hype-stock-counter-page .preset-card.active {
          border-color: #005bd3;
          box-shadow: 0 0 0 1px #005bd3;
          background: #f0f7ff;
        }
        hype-stock-counter-page .preset-card-name {
          font-size: 12px;
          font-weight: 600;
          color: #303030;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        hype-stock-counter-page .preset-swatches {
          display: flex;
          flex-direction: row;
          gap: 4px;
          align-items: center;
        }
        hype-stock-counter-page .preset-swatch {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1px solid rgba(0,0,0,0.15);
          flex-shrink: 0;
        }
        hype-stock-counter-page .preset-card-style {
          font-size: 10px;
          color: #8c9196;
          text-transform: capitalize;
        }
        @media (max-width: 768px) {
          hype-stock-counter-page .preview-layout {
            grid-template-columns: 1fr;
          }
          hype-stock-counter-page .preview-panel {
            position: static;
            order: -1;
          }
        }
      </style>
      <s-page heading="Stock Counter">
        <s-button
          slot="primary-action"
          variant="primary"
          class="${this._saveFlash ? 'save-btn-success' : ''}"
          ?loading=${this._saving}
          @click=${() => void this._handleSave()}
        >${this._saveFlash ? '✓ Saved' : 'Save'}</s-button>
        ${(() => { const shop = getShopDomain(); return shop ? html`<s-button slot="secondary-actions" variant="tertiary" @click=${() => window.open(`https://${shop}`, '_blank')} aria-label="Preview widget on your live store">Preview on store</s-button>` : ''; })()}

        ${this._saveSuccess ? html`
          <s-banner
            tone="success"
            dismissible
            @dismiss=${() => { this._saveSuccess = false; }}
          >Configuration saved successfully.</s-banner>
        ` : ''}

        ${this._saveError ? html`
          <s-banner
            tone="critical"
            dismissible
            @dismiss=${() => { this._saveError = ''; }}
          >${this._saveError}</s-banner>
        ` : ''}

        <div class="preview-layout">
          <div class="config-column">
            <s-section heading="Quick Start" class="${this._enabled ? '' : 'disabled-overlay'}">
              <div class="preset-grid">
                ${STYLE_PRESETS.map(preset => {
                  const isActive =
                    preset.display_style === this._display_style &&
                    preset.bg_color === this._bg_color &&
                    preset.text_color === this._text_color &&
                    preset.accent_color === this._accent_color;
                  return html`
                    <div
                      class="preset-card ${isActive ? 'active' : ''}"
                      role="button"
                      tabindex="0"
                      @click=${() => this._applyPreset(preset)}
                      @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._applyPreset(preset); } }}
                      title=${preset.description}
                    >
                      <div class="preset-card-name">${preset.name}</div>
                      <div class="preset-swatches">
                        <div class="preset-swatch" style="background:${preset.bg_color}"></div>
                        <div class="preset-swatch" style="background:${preset.text_color}"></div>
                        <div class="preset-swatch" style="background:${preset.accent_color}"></div>
                      </div>
                      <div class="preset-card-style">${preset.display_style}</div>
                    </div>
                  `;
                })}
              </div>
            </s-section>

            <s-section heading="Display">
              <s-stack gap="base">
                <s-checkbox
                  label="Enable widget"
                  ?checked=${this._enabled}
                  @change=${(e: Event) => void this._handleToggle((e.currentTarget as HTMLInputElement).checked)}
                ></s-checkbox>

                <div class="${this._enabled ? '' : 'disabled-overlay'}">
                  <s-stack gap="base">
                    <s-text-field
                      type="number"
                      label="Stock threshold"
                      help-text="Badge appears when inventory is at or below this."
                      min="1"
                      max="100"
                      step="1"
                      value=${String(this._threshold)}
                      @change=${(e: Event) => { this._threshold = Number((e.currentTarget as HTMLInputElement).value); }}
                    ></s-text-field>

                    <s-text-field
                      label="Message template"
                      help-text="Use {count} for the stock number."
                      value=${this._message_template}
                      @change=${(e: Event) => { this._message_template = (e.currentTarget as HTMLInputElement).value; }}
                    ></s-text-field>

                    <s-text-field
                      label="Sold out message"
                      help-text="Shown when stock reaches zero."
                      value=${this._sold_out_message}
                      @change=${(e: Event) => { this._sold_out_message = (e.currentTarget as HTMLInputElement).value; }}
                    ></s-text-field>

                    <s-checkbox
                      label="Hide when sold out"
                      help-text="Hide the badge entirely when stock is zero instead of showing the sold-out message."
                      ?checked=${this._hide_when_zero}
                      @change=${(e: Event) => { this._hide_when_zero = (e.currentTarget as HTMLInputElement).checked; }}
                    ></s-checkbox>

                    <s-select
                      label="Display style"
                      value=${this._display_style}
                      @change=${(e: Event) => { this._display_style = (e.currentTarget as HTMLInputElement).value; }}
                    >
                      <option value="badge">Badge</option>
                      <option value="inline">Inline text</option>
                    </s-select>
                  </s-stack>
                </div>
              </s-stack>
            </s-section>

            <s-section heading="Colors" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-stack gap="base">
                  <s-text>Background</s-text>
                  <s-color-picker
                    value=${this._bg_color}
                    @input=${(e: Event) => this._handleColor('bg_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>
                <s-stack gap="base">
                  <s-text>Text</s-text>
                  <s-color-picker
                    value=${this._text_color}
                    @input=${(e: Event) => this._handleColor('text_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>
                <s-stack gap="base">
                  <s-text>Accent / count</s-text>
                  <s-color-picker
                    value=${this._accent_color}
                    @input=${(e: Event) => this._handleColor('accent_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>
              </s-stack>
            </s-section>

            <s-section heading="Custom CSS" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                ${this._billing && !(this._billing.features as Record<string, boolean>).custom_css ? html`
                  <s-banner tone="info">
                    Custom CSS is available on the Pro plan.
                    <a href="/billing">Upgrade to Pro</a> to inject custom styles.
                  </s-banner>
                ` : ''}
                <textarea
                  rows="6"
                  placeholder=".badge { border-radius: 8px; }"
                  .value=${this._custom_css}
                  ?disabled=${!!(this._billing && !(this._billing.features as Record<string, boolean>).custom_css)}
                  @input=${(e: Event) => { this._custom_css = (e.currentTarget as HTMLTextAreaElement).value; }}
                  style="width:100%;font-family:monospace;font-size:13px;padding:8px;border:1px solid #d2d5d8;border-radius:8px;resize:vertical;box-sizing:border-box;"
                ></textarea>
                <s-text tone="subdued" variant="bodySm">
                  CSS is scoped to this widget's Shadow DOM. Use class names like .badge, .count, .icon.
                </s-text>
              </s-stack>
            </s-section>
          </div>

          <div class="preview-panel">
            <s-section heading="Preview">
              <div class="preview-state-buttons">
                <s-button
                  size="slim"
                  variant=${this._previewState === 'low' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'low'; }}
                >Low stock (3)</s-button>
                <s-button
                  size="slim"
                  variant=${this._previewState === 'very-low' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'very-low'; }}
                >Very low (1)</s-button>
                <s-button
                  size="slim"
                  variant=${this._previewState === 'sold-out' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'sold-out'; }}
                >Sold out</s-button>
              </div>
              <div class="preview-frame ${this._saveFlash ? 'preview-pulse' : ''}">
                <hype-stock-counter
                  inventory-quantity=${this._getPreviewQuantity()}
                  threshold=${String(this._threshold)}
                  message-template=${this._message_template}
                  sold-out-message=${this._sold_out_message}
                  hide-when-zero=${String(this._hide_when_zero)}
                  bg-color=${this._bg_color}
                  text-color=${this._text_color}
                  accent-color=${this._accent_color}
                  display-style=${this._display_style}
                ></hype-stock-counter>
              </div>
            </s-section>
          </div>
        </div>
      </s-page>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hype-stock-counter-page': StockCounterPage;
  }
}
